import { compose, isDateOrNullish } from '@monorepo/shared/utils/validators';
import { isValid } from 'date-fns';
import { NullableDate } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useField, UseFieldConfig } from 'react-final-form';
import { DatePicker } from '../dateAndTimePickers';

type DatePickerFieldProps = Omit<
  React.ComponentProps<typeof DatePicker>,
  'value' | 'onChange' | 'error' | 'errorText'
> & {
  initialValue?: Date;
  name: string;
  validate?: UseFieldConfig<Date>['validate'];
  forceShowErrors?: boolean;
  // Applied only when the picker hands back a fully valid Date. Invalid dates
  // are passed through unchanged so MUI's reset effect leaves partial text
  // alone while the user is mid-edit.
  formatDate?: (date: Date) => Date;
  onChange?: (date: Date | null) => void;
};

export function DatePickerField({
  name,
  validate,
  initialValue,
  forceShowErrors,
  formatDate,
  onChange: onChangeProp,
  ...rest
}: DatePickerFieldProps) {
  const composedValidate = useMemo(
    () => (validate ? compose(validate, isDateOrNullish) : isDateOrNullish),
    [validate],
  );

  const { input, meta } = useField<NullableDate>(name, {
    validate: composedValidate,
    initialValue,
  });

  const onChange = useCallback(
    (event: { target: { value: NullableDate } }) => {
      const date = event.target.value;
      const next =
        formatDate && date && isValid(date) ? formatDate(date) : date;
      input.onChange(next);
      onChangeProp?.(next);
    },
    [input, formatDate, onChangeProp],
  );

  const hasError =
    (forceShowErrors || meta.touched || meta.modified) && !!meta.error;
  return (
    <DatePicker
      {...rest}
      error={hasError}
      errorText={hasError ? meta.error : undefined}
      value={input.value}
      onChange={onChange}
      name={name}
    />
  );
}
